import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import {
  Plus, Eye, Edit2, Trash2, X, AlertCircle, IndianRupee,
  FileText, BarChart2, ChevronDown, Calendar, Download,
  TrendingUp, Users, Banknote, Clock, CheckCircle2,
  Printer, Search, Filter
} from "lucide-react";
import "./worker.css";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "—";

// ─── Main Component ────────────────────────────────────────────────────────────
export default function WorkerManagement() {
  const navigate = useNavigate();

  const [workers, setWorkers] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Payment modal
  const [paymentWorker, setPaymentWorker] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [payRemark, setPayRemark] = useState("");
  const [payError, setPayError] = useState("");
  const [paySuccess, setPaySuccess] = useState(false);

  // Worker report modal
  const [workerReport, setWorkerReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Site report modal
  const [showSiteReport, setShowSiteReport] = useState(false);
  const [siteReport, setSiteReport] = useState(null);
  const [siteReportLoading, setSiteReportLoading] = useState(false);
  const [reportSiteName, setReportSiteName] = useState("");
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Filters
  const [filterSite, setFilterSite] = useState("");
  const [filterType, setFilterType] = useState("");
  const workerTypes = ["Male Coolie", "Female Coolie", "Mistri"];

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [wRes, sRes] = await Promise.all([
        axiosInstance.get("/workers/all"),
        axiosInstance.get("/show-AllProject"),
      ]);
      setAllWorkers(Array.isArray(wRes.data) ? wRes.data : []);
      setSites(Array.isArray(sRes.data) ? sRes.data : []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    let f = [...allWorkers];
    if (filterSite) f = f.filter((w) => w.siteName === filterSite);
    if (filterType) f = f.filter((w) => w.workerType === filterType);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      f = f.filter((w) => w.workerName?.toLowerCase().includes(q) || w.siteName?.toLowerCase().includes(q));
    }
    setWorkers(f);
  }, [allWorkers, filterSite, filterType, searchQuery]);

  // ── Summary Stats ──────────────────────────────────────────────────────────
  const stats = {
    total: allWorkers.length,
    totalEarned: allWorkers.reduce((s, w) => s + (w.totalAmount || 0), 0),
    totalPaid: allWorkers.reduce((s, w) => s + (w.paidAmount || 0), 0),
    totalDue: allWorkers.reduce((s, w) => s + (w.balanceAmount || 0), 0),
  };

  // ── Payment Modal ──────────────────────────────────────────────────────────
  const openPaymentModal = async (worker) => {
    setPaymentWorker(worker);
    setShowAddForm(false);
    setPayAmount(""); setPayDate(new Date().toISOString().split("T")[0]);
    setPayRemark(""); setPayError(""); setPaySuccess(false);
    setPayLoading(true);
    try {
      const res = await axiosInstance.get(`/workers/${worker.id}/payments`);
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch { setPayments([]); }
    finally { setPayLoading(false); }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!payAmount || payAmount <= 0) return setPayError("Enter a valid amount");
    setPayLoading(true); setPayError("");
    const params = new URLSearchParams();
    params.append("amountPaid", payAmount);
    params.append("paymentDate", payDate);
    if (payRemark.trim()) params.append("remark", payRemark.trim());
    try {
      await axiosInstance.post(`/workers/${paymentWorker.id}/addPayment?${params}`, {}, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      setPaySuccess(true); setPayAmount(""); setPayRemark(""); setShowAddForm(false);
      await Promise.all([openPaymentModal(paymentWorker), fetchData()]);
    } catch (err) {
      setPayError(err.response?.data?.message || "Failed to add payment");
    } finally { setPayLoading(false); }
  };

  // ── Worker Report ──────────────────────────────────────────────────────────
  const openWorkerReport = async (worker) => {
    setReportLoading(true);
    setWorkerReport(null);
    try {
      const res = await axiosInstance.get(`/workers/worker-report/${worker.id}`);
      setWorkerReport(res.data);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to load report");
    } finally { setReportLoading(false); }
  };

  // ── Site Report ────────────────────────────────────────────────────────────
  const fetchSiteReport = async () => {
    if (!reportSiteName) return;
    setSiteReportLoading(true); setSiteReport(null);
    try {
      const params = new URLSearchParams({ siteName: reportSiteName });
      if (reportStartDate) params.append("startDate", reportStartDate);
      if (reportEndDate) params.append("endDate", reportEndDate);
      const res = await axiosInstance.get(`/workers/site?${params}`);
      setSiteReport(res.data);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to load site report");
    } finally { setSiteReportLoading(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await axiosInstance.delete(`/workers/delete/${deleteTarget.id}`);
      setDeleteTarget(null); fetchData();
    } catch (err) { alert(err.response?.data?.message || "Failed to delete"); }
  };

  if (loading) return (
    <div className="wm-loading">
      <div className="wm-spinner" />
      <span>Loading workers…</span>
    </div>
  );
  if (error) return (
    <div className="wm-error">
      <AlertCircle size={20} />
      <span>{error}</span>
    </div>
  );

  return (
    <div className="wm-root">

      {/* ── Page Header ───────────────────────────────────────────── */}
      <div className="wm-header">
        <div className="wm-header-left">
          <h1>Worker Management</h1>
          <p>Monitor attendance, wages, payments, and outstanding balances</p>
        </div>
        <div className="wm-header-actions">
          <button className="wm-btn-outline" onClick={() => setShowSiteReport(true)}>
            <BarChart2 size={16} /> Site Report
          </button>
          <Link to="/add-worker" className="wm-btn-primary">
            <Plus size={16} /> Add Worker
          </Link>
        </div>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div className="wm-stats">
        <StatCard icon={<Users size={18} />} label="Total Workers" value={stats.total} color="blue" />
        <StatCard icon={<TrendingUp size={18} />} label="Total Earned" value={fmt(stats.totalEarned)} color="teal" />
        <StatCard icon={<Banknote size={18} />} label="Total Paid" value={fmt(stats.totalPaid)} color="green" />
        <StatCard icon={<Clock size={18} />} label="Balance Due" value={fmt(stats.totalDue)} color="amber" />
      </div>

      {/* ── Filters & Search ──────────────────────────────────────── */}
      <div className="wm-toolbar">
        <div className="wm-search">
          <Search size={16} className="wm-search-icon" />
          <input
            placeholder="Search workers or sites…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="wm-filters">
          <div className="wm-select-wrap">
            <Filter size={14} />
            <select value={filterSite} onChange={(e) => setFilterSite(e.target.value)}>
              <option value="">All Sites</option>
              {sites.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
            <ChevronDown size={14} />
          </div>
          <div className="wm-select-wrap">
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {workerTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <ChevronDown size={14} />
          </div>
          {(filterSite || filterType || searchQuery) && (
            <button className="wm-clear-btn"
              onClick={() => { setFilterSite(""); setFilterType(""); setSearchQuery(""); }}>
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Workers Table ─────────────────────────────────────────── */}
      {workers.length === 0 ? (
        <div className="wm-empty">
          <div className="wm-empty-icon"><Users size={40} /></div>
          <h3>No workers found</h3>
          <p>Try adjusting your filters or add a new worker</p>
        </div>
      ) : (
        <div className="wm-table-wrap">
          <table className="wm-table">
            <thead>
              <tr>
                <th>Worker</th>
                <th>Type</th>
                <th>Site</th>
                <th>Daily Wage</th>
                <th>Days</th>
                <th>Total Earned</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workers.map((w) => (
                <tr key={w.id}>
                  <td>
                    <div className="wm-worker-cell">
                      <div className="wm-avatar">{w.workerName?.[0]?.toUpperCase() || "?"}</div>
                      <div>
                        <div className="wm-worker-name">{w.workerName}</div>
                        <div className="wm-worker-gender">{w.gender || ""}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="wm-badge type">{w.workerType || "—"}</span></td>
                  <td><span className="wm-site">{w.siteName || "—"}</span></td>
                  <td className="wm-num">{fmt(w.dailyWage)}</td>
                  <td className="wm-num">{w.daysWorked || 0}</td>
                  <td className="wm-num total">{fmt(w.totalAmount)}</td>
                  <td className="wm-num paid">{fmt(w.paidAmount)}</td>
                  <td className="wm-num">
                    <span className={`wm-balance ${(w.balanceAmount || 0) > 0 ? "due" : "clear"}`}>
                      {(w.balanceAmount || 0) > 0 ? fmt(w.balanceAmount) : "Settled"}
                    </span>
                  </td>
                  <td>
                    <div className="wm-actions">
                      <ActionBtn icon={<Eye size={15} />} label="Payments" color="blue"
                        onClick={() => openPaymentModal(w)} />
                      <ActionBtn icon={<FileText size={15} />} label="Report" color="teal"
                        onClick={() => openWorkerReport(w)} />
                      <ActionBtn icon={<Edit2 size={15} />} label="Edit" color="gray"
                        onClick={() => navigate(`/add-worker/${w.id}`)} />
                      <ActionBtn icon={<Trash2 size={15} />} label="Delete" color="red"
                        onClick={() => setDeleteTarget(w)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="wm-table-footer">
            Showing <strong>{workers.length}</strong> of <strong>{allWorkers.length}</strong> workers
          </div>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────── */}
      {deleteTarget && (
        <DeleteModal worker={deleteTarget} onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)} />
      )}

      {paymentWorker && (
        <PaymentModal
          worker={paymentWorker} payments={payments} loading={payLoading}
          showAddForm={showAddForm} setShowAddForm={setShowAddForm}
          payAmount={payAmount} setPayAmount={setPayAmount}
          payDate={payDate} setPayDate={setPayDate}
          payRemark={payRemark} setPayRemark={setPayRemark}
          payError={payError} paySuccess={paySuccess}
          onAddPayment={handleAddPayment}
          onClose={() => setPaymentWorker(null)}
        />
      )}

      {(workerReport || reportLoading) && (
        <WorkerReportModal
          report={workerReport}
          loading={reportLoading}
          onClose={() => setWorkerReport(null)}
        />
      )}

      {showSiteReport && (
        <SiteReportModal
          sites={sites}
          siteName={reportSiteName} setSiteName={setReportSiteName}
          startDate={reportStartDate} setStartDate={setReportStartDate}
          endDate={reportEndDate} setEndDate={setReportEndDate}
          report={siteReport} loading={siteReportLoading}
          onFetch={fetchSiteReport}
          onClose={() => { setShowSiteReport(false); setSiteReport(null); setReportSiteName(""); }}
        />
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, color }) => (
  <div className={`wm-stat-card wm-stat-${color}`}>
    <div className="wm-stat-icon">{icon}</div>
    <div className="wm-stat-body">
      <div className="wm-stat-label">{label}</div>
      <div className="wm-stat-value">{value}</div>
    </div>
  </div>
);

const ActionBtn = ({ icon, label, color, onClick }) => (
  <button className={`wm-action-btn wm-action-${color}`} onClick={onClick} title={label}>
    {icon}
  </button>
);

// ─── Delete Modal ─────────────────────────────────────────────────────────────
const DeleteModal = ({ worker, onConfirm, onCancel }) => {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onCancel]);

  return (
    <Backdrop onClick={onCancel}>
      <div className="wm-modal wm-modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="wm-modal-danger-icon"><AlertCircle size={36} /></div>
        <h2>Delete Worker</h2>
        <p>You are about to permanently delete <strong>{worker.workerName}</strong>.<br />This action cannot be undone.</p>
        <div className="wm-modal-footer">
          <button className="wm-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="wm-btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </Backdrop>
  );
};

// ─── Payment Modal ─────────────────────────────────────────────────────────────
const PaymentModal = ({
  worker, payments, loading, showAddForm, setShowAddForm,
  payAmount, setPayAmount, payDate, setPayDate,
  payRemark, setPayRemark, payError, paySuccess,
  onAddPayment, onClose,
}) => {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <Backdrop onClick={onClose}>
      <div className="wm-modal wm-modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="wm-modal-header">
          <div>
            <h2>Payment History</h2>
            <p>{worker.workerName} · {worker.siteName || "No Site"}</p>
          </div>
          <button className="wm-close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="wm-worker-summary-bar">
          <SummaryPill label="Type" value={worker.workerType || "—"} />
          <SummaryPill label="Days Worked" value={worker.daysWorked || 0} />
          <SummaryPill label="Total Earned" value={fmt(worker.totalAmount)} />
          <SummaryPill label="Paid" value={fmt(worker.paidAmount)} color="green" />
          <SummaryPill label="Balance" value={fmt(worker.balanceAmount)} color={(worker.balanceAmount || 0) > 0 ? "amber" : "green"} />
        </div>

        {showAddForm ? (
          <form onSubmit={onAddPayment} className="wm-pay-form">
            <h3 className="wm-section-title">Record New Payment</h3>
            {paySuccess && <div className="wm-alert success"><CheckCircle2 size={16} /> Payment recorded successfully</div>}
            {payError && <div className="wm-alert error"><AlertCircle size={16} /> {payError}</div>}
            <div className="wm-form-grid">
              <div className="wm-form-group">
                <label>Amount (₹) <span>*</span></label>
                <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0" min="1" required autoFocus />
              </div>
              <div className="wm-form-group">
                <label>Date <span>*</span></label>
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} required />
              </div>
            </div>
            <div className="wm-form-group">
              <label>Remark <span className="wm-optional">(optional)</span></label>
              <input type="text" value={payRemark} onChange={(e) => setPayRemark(e.target.value)}
                placeholder="e.g. Advance, Bonus, Full Settlement" />
            </div>
            <div className="wm-modal-footer">
              <button type="button" className="wm-btn-ghost" onClick={() => setShowAddForm(false)}>Back</button>
              <button type="submit" className="wm-btn-primary" disabled={loading}>
                {loading ? "Saving…" : "Save Payment"}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="wm-payments-list">
              {loading ? (
                <div className="wm-inner-loading"><div className="wm-spinner sm" /><span>Loading…</span></div>
              ) : payments.length === 0 ? (
                <div className="wm-inner-empty">
                  <IndianRupee size={32} />
                  <p>No payments recorded yet</p>
                </div>
              ) : (
                payments.map((p, i) => (
                  <div key={i} className="wm-payment-row">
                    <div className="wm-payment-dot" />
                    <div className="wm-payment-info">
                      <span className="wm-payment-date">{fmtDate(p.paymentDate || p.date)}</span>
                      {p.remark && <span className="wm-payment-remark">{p.remark}</span>}
                    </div>
                    <span className="wm-payment-amount">{fmt(p.amountPaid || p.amount)}</span>
                  </div>
                ))
              )}
            </div>
            <div className="wm-modal-footer">
              <button className="wm-btn-ghost" onClick={onClose}>Close</button>
              <button className="wm-btn-primary" onClick={() => setShowAddForm(true)}>
                <Plus size={15} /> Add Payment
              </button>
            </div>
          </>
        )}
      </div>
    </Backdrop>
  );
};

// ─── Worker Report Modal ──────────────────────────────────────────────────────
const WorkerReportModal = ({ report, loading, onClose }) => {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Worker Report – ${report?.workerName || ""}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', sans-serif; color: #1a1a1a; padding: 32px; }
        h1 { font-size: 22px; font-weight: 600; margin-bottom: 4px; }
        h2 { font-size: 15px; font-weight: 600; margin: 24px 0 12px; color: #374151; border-bottom: 1.5px solid #e5e7eb; padding-bottom: 6px; }
        .meta { color: #6b7280; font-size: 13px; margin-bottom: 24px; }
        .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
        .stat-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; }
        .stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
        .stat-val { font-size: 18px; font-weight: 600; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 8px 12px; background: #f9fafb; font-weight: 600; font-size: 11px; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; }
        .full { background: #dcfce7; color: #166534; }
        .half { background: #fef9c3; color: #854d0e; }
        .absent { background: #fee2e2; color: #991b1b; }
        @media print { body { padding: 16px; } }
      </style>
      </head><body>${content}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <Backdrop onClick={onClose}>
      <div className="wm-modal wm-modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="wm-modal-header">
          <div>
            <h2>Worker Report</h2>
            {report && <p>{report.workerName} · {report.siteName}</p>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {report && (
              <button className="wm-btn-outline sm" onClick={handlePrint}>
                <Printer size={15} /> Print / PDF
              </button>
            )}
            <button className="wm-close-btn" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {loading ? (
          <div className="wm-inner-loading" style={{ minHeight: 200 }}>
            <div className="wm-spinner" /><span>Loading report…</span>
          </div>
        ) : report ? (
          <div className="wm-report-body" ref={printRef}>
            <h1>{report.workerName}</h1>
            <p className="meta">Site: {report.siteName}</p>

            <div className="stat-grid">
              <ReportStat label="Days Worked" value={report.totalDaysWorked || 0} />
              <ReportStat label="Total Earned" value={fmt(report.totalWageAmount)} />
              <ReportStat label="Total Paid" value={fmt(report.totalPaidAmount)} color="green" />
              <ReportStat label="Balance Due" value={fmt(report.balanceAmount)} color={(report.balanceAmount || 0) > 0 ? "amber" : "green"} />
            </div>

            {/* Attendance */}
            {report.attendance?.length > 0 && (
              <>
                <h2>Attendance Log</h2>
                <table>
                  <thead>
                    <tr><th>Date</th><th>Attendance</th></tr>
                  </thead>
                  <tbody>
                    {report.attendance.map((a, i) => (
                      <tr key={i}>
                        <td>{fmtDate(a.date)}</td>
                        <td>
                          <span className={`badge ${a.attendanceType === "FULL_DAY" ? "full" : a.attendanceType === "HALF_DAY" ? "half" : "absent"}`}>
                            {a.attendanceType?.replace("_", " ") || "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Payments */}
            {report.payments?.length > 0 && (
              <>
                <h2 style={{ marginTop: 24 }}>Payment History</h2>
                <table>
                  <thead>
                    <tr><th>Date</th><th>Amount Paid</th><th>Remark</th></tr>
                  </thead>
                  <tbody>
                    {report.payments.map((p, i) => (
                      <tr key={i}>
                        <td>{fmtDate(p.paymentDate)}</td>
                        <td style={{ fontWeight: 600 }}>{fmt(p.amountPaid)}</td>
                        <td style={{ color: "#6b7280" }}>{p.remark || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        ) : null}
      </div>
    </Backdrop>
  );
};

// ─── Site Report Modal ────────────────────────────────────────────────────────
const SiteReportModal = ({
  sites, siteName, setSiteName,
  startDate, setStartDate, endDate, setEndDate,
  report, loading, onFetch, onClose,
}) => {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Site Report – ${report?.siteName || ""}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', sans-serif; color: #1a1a1a; padding: 32px; }
        h1 { font-size: 22px; font-weight: 600; margin-bottom: 4px; }
        h2 { font-size: 15px; font-weight: 600; margin: 24px 0 12px; color: #374151; border-bottom: 1.5px solid #e5e7eb; padding-bottom: 6px; }
        .meta { color: #6b7280; font-size: 13px; margin-bottom: 24px; }
        .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
        .stat-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; }
        .stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
        .stat-val { font-size: 18px; font-weight: 600; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 8px 12px; background: #f9fafb; font-weight: 600; font-size: 11px; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; }
        .due { color: #b45309; font-weight: 600; }
        .clear { color: #16a34a; }
        @media print { body { padding: 16px; } }
      </style>
      </head><body>${content}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const siteWorkers = report?.workers || [];
  const totalSalary = siteWorkers.reduce((s, w) => s + (w.totalSalary || 0), 0);
  const totalPaid = siteWorkers.reduce((s, w) => s + (w.totalPaid || 0), 0);
  const totalBal = siteWorkers.reduce((s, w) => s + (w.balance || 0), 0);

  return (
    <Backdrop onClick={onClose}>
      <div className="wm-modal wm-modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="wm-modal-header">
          <div>
            <h2>Site Report</h2>
            {report && <p>{report.siteName}</p>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {report && (
              <button className="wm-btn-outline sm" onClick={handlePrint}>
                <Printer size={15} /> Print / PDF
              </button>
            )}
            <button className="wm-close-btn" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {/* Filters */}
        <div className="wm-site-report-filters">
          <div className="wm-form-group">
            <label>Site <span>*</span></label>
            <div className="wm-select-wrap full">
              <select value={siteName} onChange={(e) => setSiteName(e.target.value)}>
                <option value="">Select a site…</option>
                {sites.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
              <ChevronDown size={14} />
            </div>
          </div>
          <div className="wm-form-group">
            <label>From Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="wm-form-group">
            <label>To Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <button className="wm-btn-primary" onClick={onFetch} disabled={!siteName || loading}>
            {loading ? "Loading…" : "Generate"}
          </button>
        </div>

        {loading && (
          <div className="wm-inner-loading"><div className="wm-spinner" /><span>Generating report…</span></div>
        )}

        {report && !loading && (
          <div className="wm-report-body" ref={printRef}>
            <h1>{report.siteName}</h1>
            <p className="meta">
              {startDate && endDate ? `${fmtDate(startDate)} – ${fmtDate(endDate)}` :
               startDate ? `From ${fmtDate(startDate)}` :
               endDate ? `Up to ${fmtDate(endDate)}` : "All time"}
            </p>

            <div className="stat-grid">
              <ReportStat label="Workers" value={siteWorkers.length} />
              <ReportStat label="Total Salary" value={fmt(totalSalary)} />
              <ReportStat label="Total Paid" value={fmt(totalPaid)} color="green" />
              <ReportStat label="Total Balance" value={fmt(totalBal)} color={totalBal > 0 ? "amber" : "green"} />
            </div>

            <h2>Worker Breakdown</h2>
            <table>
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>Days Worked</th>
                  <th>Total Salary</th>
                  <th>Paid</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {siteWorkers.map((w, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{w.workerName}</td>
                    <td>{w.daysWorked || 0}</td>
                    <td>{fmt(w.totalSalary)}</td>
                    <td className="clear">{fmt(w.totalPaid)}</td>
                    <td className={(w.balance || 0) > 0 ? "due" : "clear"}>
                      {(w.balance || 0) > 0 ? fmt(w.balance) : "Settled"}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid #e5e7eb", background: "#f9fafb" }}>
                  <td style={{ fontWeight: 700 }}>Total</td>
                  <td />
                  <td style={{ fontWeight: 700 }}>{fmt(totalSalary)}</td>
                  <td style={{ fontWeight: 700, color: "#16a34a" }}>{fmt(totalPaid)}</td>
                  <td style={{ fontWeight: 700, color: totalBal > 0 ? "#b45309" : "#16a34a" }}>{fmt(totalBal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Backdrop>
  );
};

// ─── Micro-components ─────────────────────────────────────────────────────────
const Backdrop = ({ children, onClick }) => (
  <div className="wm-backdrop" onClick={onClick}>{children}</div>
);

const SummaryPill = ({ label, value, color }) => (
  <div className={`wm-pill${color ? ` wm-pill-${color}` : ""}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const ReportStat = ({ label, value, color }) => (
  <div className={`stat-box${color ? ` rs-${color}` : ""}`}>
    <div className="stat-label">{label}</div>
    <div className="stat-val">{value}</div>
  </div>
);